
import { useNotifications, useMarkAllNotificationsRead } from '../hooks/useNotifications';
import { useEffect, useRef, useState } from 'react';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import DOMPurify from 'dompurify';
dayjs.extend(relativeTime);

const notificationSound = '/notification.mp3'; 

export default function NotificationDropdown({ open, onClose }) {
  const dropdownOptions = useMemo(() => ({
    page: 1,
    pageSize: 10
  }), []); 

  const { data, isLoading, refetch } = useNotifications(dropdownOptions);
  
  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);
  const markAllRead = useMarkAllNotificationsRead();
  const [lastIds, setLastIds] = useState([]);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!isLoading && data?.notifications) {
      const newIds = data.notifications.map(n => n._id);
      if (open && lastIds.length && newIds[0] !== lastIds[0]) {
        // Play sound on new notification only if dropdown is open (user interacted)
        audioRef.current?.play();
      }
      setLastIds(newIds);
    }
  }, [data, isLoading, open]);

  const handleMarkAllRead = () => {
    if (!markAllRead.isLoading) {
      markAllRead.mutate(undefined, {
        onSuccess: () => {
          refetch();
        }
      });
    }
  };

  return (
    <div className={`absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 transition-all duration-300 ${open ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}
      style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
      <audio ref={audioRef} src={notificationSound} preload="auto" />
      <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b">
        <span className="font-semibold text-lg text-slate-800">Notifications</span>
        <button className="text-blue-600 text-sm font-medium hover:underline" onClick={onClose}>Close</button>
      </div>
      <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
        {isLoading ? (
          <div className="p-6 text-center text-slate-400 animate-pulse">Loading...</div>
        ) : !data?.notifications || data.notifications.length === 0 ? (
          <div className="p-6 text-center text-slate-400">No notifications</div>
        ) : data.notifications.map((notif) => (
          <div key={notif._id} className={`flex gap-3 px-4 py-3 items-start group transition-colors duration-200 ${notif.read ? 'bg-white' : 'bg-blue-50/60'}`}>
            {notif.image && (
              <img src={notif.image} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-200 shadow-sm" />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-800 text-sm truncate">
                {notif.richContent ? (
                  <span
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notif.richContent) }}
                  />
                ) : notif.message}
              </div>
              <div className="text-xs text-slate-500 mt-1">{dayjs(notif.createdAt).fromNow()}</div>
              {Array.isArray(notif.actions) && notif.actions.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {notif.actions.map((action, idx) => (
                    <button
                      key={idx}
                      className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-200 transition"
                      onClick={() => window.dispatchEvent(new CustomEvent('notificationAction', { detail: { notification: notif, action } }))}
                    >
                      {action.label === "route" ? "Click here" : action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {notif.link && (
              <a href={notif.link} className="ml-2 text-blue-500 text-xs font-semibold hover:underline">View</a>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-4 py-2 border-t bg-slate-50 rounded-b-xl">
        <button onClick={handleMarkAllRead} disabled={markAllRead.isLoading} className={`text-blue-600 text-sm font-medium hover:underline transition ${markAllRead.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}>Mark all as read</button>
        <a href="/notifications" className="text-blue-600 text-sm font-medium hover:underline transition">View all</a>
      </div>
    </div>
  );
}
